import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg, plural, t } from '@lingui/core/macro'
import { DOC_CHAT_SEARCH_FAILED_FALLBACK } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type { ResponseSegment } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  DocChatEvent,
  DocChatResponseEvent,
} from 'features/knowledgeDocumentViewer/types'
import { retrievedWorkOrderRows } from 'features/knowledgeDocumentViewer/workOrders/workOrder'

export const DOC_CHAT_FALLBACK_NOTICE: MessageDescriptor = msg`AI search could not complete. Showing standard document search results — this is not a full AI answer.`

export const DOC_CHAT_SEARCH_FAILED_NOTICE: MessageDescriptor = msg`Search failed. Try again.`

export const DOC_CHAT_STOPPED_NOTICE: MessageDescriptor = msg`Stopped.`

export function isDocChatSearchPlanThought(event: DocChatEvent): boolean {
  return (
    event.type === 'thought' &&
    event.message.trimStart().startsWith('Planned a blended search:')
  )
}

export function getDocChatErrorNotice(message?: string): string {
  const trimmed = message?.trim()
  if (trimmed && trimmed !== DOC_CHAT_SEARCH_FAILED_FALLBACK) return trimmed
  return i18n._(DOC_CHAT_SEARCH_FAILED_NOTICE)
}

/** Human-readable result count, e.g. "1 result" or "1,234 results". */
export function formatResultLabel(count: number): string {
  return plural(count, { one: '# result', other: '# results' })
}

/**
 * Whether a completed response has a document search status to show. A turn
 * that ran no document search has none. A fallback always searched, and older
 * backends omit `searched_documents`.
 */
export function shouldShowDocChatSearchStatus(
  responseEvent: DocChatResponseEvent
): boolean {
  const { is_fallback, searched_documents } = responseEvent.metadata
  return is_fallback || searched_documents !== false
}

/**
 * Shared fallback status text for a completed docchat response, used for
 * clipboard copy, collapsed thought summaries, and header timeline rows.
 */
export function getDocChatResponseStatusText(
  responseEvent: DocChatResponseEvent
): string {
  if (
    !shouldShowDocChatSearchStatus(responseEvent) &&
    responseEvent.inspection_results?.length
  ) {
    return t`Inspection results ready`
  }
  const workOrderCount = retrievedWorkOrderRows(responseEvent).length
  if (responseEvent.cmms || workOrderCount) {
    const count = workOrderCount + (responseEvent.results?.length ?? 0)
    return plural(count, {
      one: 'Retrieved # source',
      other: 'Retrieved # sources',
    })
  }
  const timeSeries = responseEvent.time_series
  if (timeSeries) {
    const seriesCount = timeSeries.series.length
    const datapointLabel = plural(seriesCount, {
      one: '# datapoint',
      other: '# datapoints',
    })
    return t`Read live values for ${datapointLabel}`
  }
  if (!shouldShowDocChatSearchStatus(responseEvent)) {
    return t`Thoughts`
  }
  const resultLabel = formatResultLabel(responseEvent.resultCount)
  if (responseEvent.metadata.is_fallback) {
    return t`AI search could not complete — found ${resultLabel} via standard search`
  }
  return t`Updated search query and found ${resultLabel}`
}

/**
 * The follow-up questions of an answer, from its suggestions segments: trimmed,
 * without blanks or repeats. A null or missing list gives none.
 */
export function getDocChatSuggestedQuestions(responseEvent: {
  response_segments?: ResponseSegment[]
}): string[] {
  const questions = (responseEvent.response_segments ?? [])
    .filter((segment) => segment.kind === 'suggestions')
    .flatMap((segment) => segment.suggestions ?? [])
    .map((question) => question.trim())
    .filter((question) => question.length > 0)
  return [...new Set(questions)]
}

export function getDocChatResponseBodyText(responseEvent: {
  final_response?: string
  response?: string
  response_segments?: ResponseSegment[]
}): string {
  // The follow-up questions are not part of the answer. The full text can
  // still hold them, so an answer with a suggestions segment is read from its
  // other segments.
  const allSegments = responseEvent.response_segments ?? []
  const segments = allSegments
    .filter((segment) => segment.kind !== 'suggestions')
    .map((segment) => segment.text.trim())
    .filter((text) => text.length > 0)
  const hasSuggestions = allSegments.some(
    (segment) => segment.kind === 'suggestions'
  )
  if (hasSuggestions && segments.length) return segments.join('\n\n')
  const finalResponse = responseEvent.final_response?.trim()
  if (finalResponse) return finalResponse
  const response = responseEvent.response?.trim()
  if (response) return response
  if (segments.length) return segments.join('\n\n')
  return ''
}
